import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowDown,
  ArrowUpRight,
  Sparkles,
  X,
  Check,
  Heart,
  ShieldCheck,
  FileText,
  Clock,
  Moon,
  HeartPulse,
  Brain,
  Globe2,
  Compass,
  Cpu,
  Quote,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { fetchStories, fetchStory } from "@/lib/api";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WaitlistFormCore } from "@/components/landing/WaitlistFormCore";

const SURVEY_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSflKfkAfY6rAaSdLqqMC_xVzOm9qauyavaE_NOqO6q3ex3uqw/viewform?usp=header";

const DOCTOR_REQUEST_AVAILABLE = true;
const HIDE_LEGACY_HOME_QUIZ = true;
const HIDE_HOME_STAT_45 = true;
const HIDE_HOME_ARC = true;
const HIDE_HOME_PULL_QUOTE = true;
const HIDE_HOME_STORY_HELP_SHAPE = true;
const HIDE_HOME_STORY_WHY_WOMEN = true;
const HIDDEN_HOME_STORY_SLUGS = new Set([
  ...(HIDE_HOME_STORY_HELP_SHAPE ? ["help-shape-supple-survey"] : []),
  ...(HIDE_HOME_STORY_WHY_WOMEN ? ["why-women-deserve-better-care"] : []),
]);

const FAQ_ITEMS = [
  {
    q: "What are the early signs of perimenopause?",
    a: "The earliest signs are usually irregular periods, sleep disruption (especially 1–4am wake-ups), unexplained mood swings, brain fog, and new anxiety. Many women also notice their body composition changing without any change in diet, and joint aches that feel out of proportion to activity levels. These often begin 8–10 years before actual menopause.",
  },
  {
    q: "At what age does perimenopause start?",
    a: "Perimenopause most commonly begins between ages 35 and 45, though onset varies widely. Asian women tend to experience it 1–2 years earlier than Western cohorts, with subtler vasomotor symptoms. The transition typically lasts 4–10 years, ending 12 months after your final period — which averages around age 51 globally, and 48–50 for many Asian women.",
  },
  {
    q: "Is perimenopause different for Asian women?",
    a: "Yes. Asian women report perimenopause an average of 1.5–2 years earlier than white Western cohorts, with different symptom clusters — more joint pain, cognitive complaints, and sleep issues, and fewer hot flushes. Most existing menopause research was built on Western, post-menopausal women, which means standard symptom checklists often miss the way Asian bodies present.",
  },
  {
    q: "How do I know if it's perimenopause or stress?",
    a: "The distinguishing signal is pattern, not symptom. Stress fluctuates with life events; perimenopause symptoms cluster and persist even during calm periods. If you notice 3+ of these together — irregular cycles, 3am wake-ups, unexplained mood swings, brain fog, and worsening PMS — for more than 3 months, it's more likely hormonal than stress-related.",
  },
  {
    q: "Can I test for perimenopause at home?",
    a: "A single home hormone test isn't reliable during perimenopause because hormone levels swing day to day — a normal FSH result on Tuesday tells you nothing about Wednesday. What actually works is tracking symptoms and cycle patterns over 2–3 months to build a picture your clinician can act on. This is what Supple's daily check-in tool is designed to do.",
  },
  {
    q: "Is it for perimenopause or menopause?",
    a: "Both. From the first irregular, confusing signs through menopause and beyond — wherever you are in the transition.",
  },
  {
    q: "When can I use it?",
    a: "It's in active development. Join the waitlist and we'll email you the moment there's something to try.",
  },
  {
    q: "What will it cost?",
    a: "Founding members use everything free during the beta, then stay on founder pricing — the lowest we'll ever offer.",
  },
  {
    q: "Is my data private?",
    a: "Your most personal notes are processed privately and never sold or used for advertising. Enterprise-grade encryption, healthcare-grade compliance.",
  },
];

const WHY_CARDS = [
  {
    icon: Globe2,
    title: "Calibrated for Asian bodies",
    body: "Asian women often hit perimenopause earlier and with quieter vasomotor symptoms. Most apps were built on Western data. Supple isn't.",
  },
  {
    icon: Compass,
    title: "Culturally fluent, not clinical-cold",
    body: "Built for the daughter, the mother, the working professional pulled in ten directions. We don't ask you to \u201Cself-care\u201D your way through. We meet you where you are.",
  },
  {
    icon: Cpu,
    title: "Science-backed, AI-personal",
    body: "A 26-question clinical screening plus an AI hormone-intelligence layer that learns your patterns — all from your phone, no blood tests required.",
  },
];

const STATS = [
  {
    value: "Up to 80%",
    label: "of women get hot flashes during the menopause transition.",
    source: "SWAN · largest US menopause study",
  },
  {
    value: "~7 years",
    label: "how long symptoms last, on average.",
    source: "SWAN",
  },
  {
    value: "45%",
    label: "say a doctor didn't take their symptoms seriously.",
    source: "The Menopause Society",
  },
  {
    value: "<5%",
    label: "of women in Asia get accurate hormonal support or diagnosis.",
    source: "Supple research",
  },
];

const CHECK_QUESTIONS = [
  {
    id: "q1",
    text: "Is your period feeling less predictable than a year ago — shorter, longer?",
  },
  {
    id: "q2",
    text: "Do you wake between 1–4am, hot or wired, and can't fall back asleep?",
  },
  {
    id: "q3",
    text: "Are sudden mood swings or 'why am I crying' moments showing up more than they used to?",
  },
];

const TIMELINE = [
  {
    stage: "Your 40s",
    title: "Perimenopause begins",
    body: "Periods get unpredictable. Sleep starts to slip, moods swing, and the first hot flashes and brain fog show up — often before you realise it's perimenopause.",
    source: null,
  },
  {
    stage: "Around 51",
    title: "Menopause",
    body: "Hot flashes and night sweats peak. Sleep takes the biggest hit: trouble sleeping climbs from about 1 in 3 women before, to around half here.",
    source: "SWAN · sleep meta-analysis",
  },
  {
    stage: "After",
    title: "Postmenopause",
    body: "For most women it eases. But hot flashes still last about seven years on average, so this stretch matters well past the finish line.",
    source: "SWAN",
  },
];

// ───────────────── SEO — Homepage title, meta, and JSON-LD ─────────────────
function applyHomepageSEO(faqItems) {
    document.title =
      "Supple — perimenopause care for Asian women";

    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(
      "name",
      "description",
      "AI-native perimenopause care for Asian women in their 30s and 40s. A 30-second check, a clinical hormone screen, and plain-language answers built for our bodies."
    );
    setMeta("property", "og:title", "Supple — perimenopause care for Asian women");
    setMeta(
      "property",
      "og:description",
      "AI-native perimenopause care for Asian women in their 30s and 40s. Built for our bodies. Not borrowed."
    );
    setMeta("property", "og:url", "https://supple.care");
    setMeta("property", "og:type", "website");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://supple.care");

    // JSON-LD — Organization + WebSite + FAQPage (combined into a single graph)
    const graph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://supple.care/#org",
          name: "Supple",
          url: "https://supple.care",
          logo: "https://supple.care/brand/supple-logo.png",
          description:
            "AI-native perimenopause care for Asian women in India and Southeast Asia.",
          founder: { "@type": "Person", name: "Shruti Gupta" },
          areaServed: ["IN", "SG", "MY", "TH", "ID", "PH", "VN"],
          contactPoint: {
            "@type": "ContactPoint",
            email: "shruti@supple.care",
            contactType: "founder",
          },
        },
        {
          "@type": "WebSite",
          "@id": "https://supple.care/#website",
          url: "https://supple.care",
          name: "Supple",
          publisher: { "@id": "https://supple.care/#org" },
          potentialAction: {
            "@type": "SearchAction",
            target: "https://supple.care/journal?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@type": "FAQPage",
          "@id": "https://supple.care/#faq",
          mainEntity: faqItems.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    };
    let script = document.getElementById("supple-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "supple-jsonld";
      document.head.appendChild(script);
    }
  script.textContent = JSON.stringify(graph);
}

function useHomepageSEO(faqItems) {
  useEffect(() => applyHomepageSEO(faqItems), [faqItems]);
}

// ───────────────── HEADER + FOOTER + WAITLIST FORM ─────────────────
// (moved to /app/frontend/src/components/landing/ so they can be shared
//  between the homepage and the /why page)

// ───────────────── HERO ─────────────────
function Hero({ onJoinClick, onStageCheck }) {
  return (
    <section
      id="top"
      className="home-hero"
      data-testid="hero-section"
    >
      <div className="home-hero__inner">
        <div className="home-hero__copy">
          <p className="home-hero__eyebrow" data-testid="hero-eyebrow">
            30 second check; Menopause-Pro Doctors
          </p>
          <h1 className="home-hero__headline" data-testid="hero-headline">
            <span className="home-hero__headline-first-line">Is it <span className="home-hero__headline-highlight">perimenopause?</span></span> Find out in 30 seconds.
          </h1>
          <p className="home-hero__subhead" data-testid="hero-subhead">
            Check your stage, get a supplement plan, and talk to a doctor. Built for you.
          </p>
          <div className="home-hero__actions">
            <a href="#check" onClick={onStageCheck} className="home-primary-action" data-testid="hero-check-stage-button">
              Check my peri/meno stage
            </a>
            <button className="home-waitlist-link" onClick={onJoinClick} data-testid="hero-join-btn">
              Join waitlist
            </button>
          </div>
          <p className="home-hero__proof" data-testid="hero-proof-line">
            45% of women say a doctor didn&apos;t take their symptoms seriously. You deserve better. (The Menopause Society)
          </p>
        </div>
        <div className="home-hero__media">
          <ThirtySecondCheck mode="hero" />
          <div className="home-hero__circle home-hero__circle--yellow" aria-hidden="true" />
          <div className="home-hero__circle home-hero__circle--blue" aria-hidden="true" />
          <picture className="home-hero__photo-frame">
            <source
              media="(max-width: 767px)"
              type="image/webp"
              srcSet="/brand/hero-asian-woman-mobile.webp"
            />
            <source
              type="image/webp"
              srcSet="/brand/hero-asian-woman-desktop.webp"
            />
            <source
              media="(max-width: 767px)"
              type="image/jpeg"
              srcSet="/brand/hero-asian-woman-mobile.jpg"
            />
            <source
              type="image/jpeg"
              srcSet="/brand/hero-asian-woman-desktop.jpg"
            />
            <img
              src="/brand/hero-asian-woman-desktop.jpg"
              alt="South Asian woman in warm, dramatic light."
              fetchPriority="high"
              decoding="async"
            />
          </picture>
        </div>
      </div>
    </section>
  );
}

// ───────────────── WHY SUPPLE (3-card) ─────────────────
function WhySupple() {
  return (
    <section
      id="why"
      className="section-pad bg-bg"
      data-testid="why-supple-section"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <p className="eyebrow mb-4">Why Supple</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]">
            Built for our bodies. <em>Not borrowed.</em>
          </h2>
        </div>

        <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {WHY_CARDS.map((card, i) => {
            const Icon = card.icon;
            const num = String(i + 1).padStart(2, "0");
            return (
              <div
                key={card.title}
                className="supple-card reveal flex flex-col"
                style={{ animationDelay: `${i * 80}ms` }}
                data-testid={`why-card-${i}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#FFF4EC] text-[#9A3F25]">
                    <Icon size={18} strokeWidth={1.75} />
                  </span>
                  <span className="font-display text-2xl text-[#EC6A4A]/70 font-medium">
                    {num}
                  </span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-medium text-[#1C1917] mb-2">
                  {card.title}
                </h3>
                <p className="text-[#44403C] leading-relaxed text-[15px] sm:text-base">
                  {card.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ───────────────── STATS ─────────────────
function Stats() {
  return (
    <section
      className="section-pad bg-bg"
      data-testid="stats-section"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <p className="eyebrow mb-4">By the numbers</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]">
            You&apos;re not imagining it. <em>And you&apos;re not alone.</em>
          </h2>
        </div>

        <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {STATS.map((s, i) => {
            if (HIDE_HOME_STAT_45 && s.value === "45%") return null;
            return (
              <div
                key={s.value}
                className="supple-card reveal"
                style={{ animationDelay: `${i * 80}ms`, padding: "1.75rem 1.5rem" }}
                data-testid={`stat-card-${i}`}
              >
                <p className="font-display text-4xl sm:text-5xl font-medium text-[#EC6A4A] leading-none">
                  {s.value}
                </p>
                <p className="mt-4 text-[#1C1917] text-[15px] sm:text-base leading-relaxed">
                  {s.label}
                </p>
                <p className="mt-3 text-xs text-[#78716C] tracking-wide">
                  {s.source}
                </p>
              </div>
            );
          })}
        </div>

        <p className="mt-10 sm:mt-12 max-w-2xl lg:max-w-none lg:whitespace-nowrap text-[15px] sm:text-base text-[#44403C] leading-relaxed">
          These patterns are real and well documented — and Supple is built
          to find them in your own data.
        </p>
      </div>
    </section>
  );
}

// ───────────────── 30-SECOND CHECK ─────────────────
function ThirtySecondCheck({ mode = "legacy" }) {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({ q1: null, q2: null, q3: null });
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [isStageGuided, setIsStageGuided] = useState(false);
  const guidanceTimerRef = useRef(null);
  const answeredCount = useMemo(
    () => Object.values(answers).filter((answer) => answer !== null).length,
    [answers]
  );
  const allAnswered = answeredCount === 3;

  function setAnswer(id, value) {
    const nextAnswers = { ...answers, [id]: value };
    const questionIndex = CHECK_QUESTIONS.findIndex((question) => question.id === id);
    setAnswers(nextAnswers);
    if (mode === "hero" && questionIndex >= 0) {
      window.setTimeout(() => {
        if (questionIndex + 1 >= CHECK_QUESTIONS.length) {
          handleSubmit(nextAnswers);
          return;
        }
        setActiveQuestion(questionIndex + 1);
      }, 300);
    }
  }

  function handleSubmit(submittedAnswers = answers) {
    // Persist locally so the future in-app intake can read these answers.
    try {
      localStorage.setItem(
        "supple.thirty_second_check",
        JSON.stringify({ ...submittedAnswers, ts: new Date().toISOString() })
      );
    } catch (e) {
      /* localStorage may be disabled — non-fatal */
    }
    navigate("/check?new=1");
  }

  useEffect(() => {
    if (mode !== "hero") return undefined;
    const resetToFirstQuestion = () => {
      setAnswers({ q1: null, q2: null, q3: null });
      setActiveQuestion(0);
      setIsStageGuided(true);
      window.clearTimeout(guidanceTimerRef.current);
      guidanceTimerRef.current = window.setTimeout(() => setIsStageGuided(false), 3000);
    };
    window.addEventListener("supple:start-stage-check", resetToFirstQuestion);
    return () => {
      window.removeEventListener("supple:start-stage-check", resetToFirstQuestion);
      window.clearTimeout(guidanceTimerRef.current);
    };
  }, [mode]);

  if (mode === "hero") {
    const currentQuestion = CHECK_QUESTIONS[activeQuestion];
    const isComplete = activeQuestion >= CHECK_QUESTIONS.length;

    return (
      <section id="check" className={`home-quiz-card${isStageGuided ? " is-guided" : ""}`} data-testid="hero-quiz-card">
        <div className="home-quiz-card__topline">
          <p data-testid="hero-quiz-progress-label">Question {Math.min(activeQuestion + 1, 3)} of 3</p>
          <div className="home-quiz-card__segments" aria-label={`Question ${Math.min(activeQuestion + 1, 3)} of 3`}>
            {CHECK_QUESTIONS.map((question, index) => (
              <span
                key={question.id}
                className={index === activeQuestion ? "is-current" : ""}
                data-testid={`hero-quiz-progress-segment-${index + 1}`}
              />
            ))}
          </div>
        </div>
        {!isComplete ? (
          <>
            <p className="home-quiz-card__question" data-testid={`hero-quiz-question-${activeQuestion + 1}`}>
              {currentQuestion.text}
            </p>
            <div className="home-quiz-card__answers">
              {["Yes", "Sometimes", "No"].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setAnswer(currentQuestion.id, option)}
                  className={answers[currentQuestion.id] === option ? "is-selected" : ""}
                  data-testid={`hero-quiz-${currentQuestion.id}-${option.toLowerCase()}`}
                >
                  {option}
                </button>
              ))}
            </div>
          </>
        ) : null}
        <p className="home-quiz-card__caption">
          Three questions. No clinic. Built from the same clinical scoring that powers our full screening.
        </p>
      </section>
    );
  }

  return (
    <section
      id={HIDE_LEGACY_HOME_QUIZ ? "legacy-check" : "check"}
      className="section-pad"
      style={{ backgroundColor: "var(--surface-alt)" }}
      data-testid="thirty-second-check-section"
      hidden={HIDE_LEGACY_HOME_QUIZ}
      aria-hidden={HIDE_LEGACY_HOME_QUIZ}
    >
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="quick-check-feature" data-testid="quick-check-feature-frame">
        <div className="quick-check-feature__topline">
          <p className="eyebrow">30-second check</p>
          <button type="button" className="quick-check-feature__arrow" onClick={() => document.getElementById("quick-check-first-question")?.scrollIntoView({ behavior: "smooth", block: "center" })} aria-label="Go to the first question" data-testid="quick-check-direction-arrow">
            <ArrowDown size={25} strokeWidth={3.25} aria-hidden="true" />
          </button>
        </div>
        <div className="max-w-2xl">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]">
            Am I in <em>perimenopause?</em>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#44403C] leading-relaxed">
            Three questions. No clinic. Built from the same clinical scoring
            that powers our full screening.
          </p>
        </div>

        <div className="mt-8 sm:mt-12 space-y-3 sm:space-y-4">
          {CHECK_QUESTIONS.map((q, i) => {
            const num = String(i + 1).padStart(2, "0");
            const value = answers[q.id];
            return (
              <div
                key={q.id}
                id={q.id === "q1" ? "quick-check-first-question" : undefined}
                className="bg-white rounded-xl sm:rounded-2xl border border-[#F1D9C6] p-4 sm:p-6"
                data-testid={`check-question-${i}`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <span className="font-display text-2xl sm:text-3xl text-[#EC6A4A]/70 font-medium flex-shrink-0 leading-none mt-0.5">
                    {num}
                  </span>
                  <div className="flex-1">
                    <p className="text-[#1C1917] text-[15px] sm:text-base leading-relaxed font-medium">
                      {q.text}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2.5">
                      {["Yes", "Sometimes", "No"].map((opt) => {
                        const selected = value === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setAnswer(q.id, opt)}
                            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 border ${
                              selected
                                ? "bg-[#EC6A4A] text-white border-[#EC6A4A] shadow-md"
                                : "bg-white text-[#44403C] border-[#F1D9C6] hover:border-[#EC6A4A] hover:text-[#9A3F25]"
                            }`}
                            data-testid={`check-${q.id}-${opt.toLowerCase()}`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-9 flex flex-col items-start gap-3">
          <button
            onClick={handleSubmit}
            disabled={!allAnswered}
            className="cta-pill cta-accent disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none"
            data-testid="check-submit-btn"
          >
            Is this normal? <ArrowUpRight size={16} />
          </button>
          <p className="text-sm text-[#78716C]">
            {allAnswered
              ? "Your answers will be carried into the 3-minute survey."
              : `Answer all three to continue (${answeredCount}/3)`}
          </p>
        </div>
        </div>
      </div>
    </section>
  );
}

// ───────────────── ARC / TIMELINE ─────────────────
function Arc() {
  if (HIDE_HOME_ARC) return null;

  return (
    <section
      className="section-pad bg-bg"
      data-testid="arc-section"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <p className="eyebrow mb-4">The arc</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]">
            Not sure what&apos;s normal? <em>You&apos;re in good company.</em>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#44403C] leading-relaxed">
            59% of women don&apos;t even know what perimenopause is before it
            starts. Here&apos;s the arc most women follow — your timing is
            your own, but the shape is remarkably common.
          </p>
        </div>

        <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 relative">
          {TIMELINE.map((t, i) => {
            const Icon = [Clock, HeartPulse, Moon][i];
            return (
              <div
                key={t.stage}
                className="supple-card reveal flex flex-col"
                style={{ animationDelay: `${i * 100}ms` }}
                data-testid={`arc-card-${i}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FFF4EC] text-[#9A3F25]">
                    <Icon size={18} strokeWidth={1.75} />
                  </span>
                  <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#9A3F25]">
                    {t.stage}
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-[1.75rem] font-medium text-[#1C1917] mb-3">
                  {t.title}
                </h3>
                <p className="text-[#44403C] leading-relaxed text-[15px] sm:text-base flex-1">
                  {t.body}
                </p>
                {t.source && (
                  <p className="mt-4 text-xs text-[#78716C] tracking-wide">
                    {t.source}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ───────────────── FOUNDER NOTE ─────────────────
function FounderNote() {
  return (
    <section
      className="section-pad"
      style={{ backgroundColor: "var(--surface-alt)" }}
      data-testid="founder-note-section"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <p className="eyebrow mb-4">A note from Supple</p>
        <h2 className="font-display text-4xl sm:text-5xl font-medium text-[#1C1917]">
          Why we&apos;re <em>building this.</em>
        </h2>
        <div className="mt-8 relative">
          <Quote
            size={36}
            className="absolute -top-2 -left-1 text-[#EC6A4A]/30"
            strokeWidth={1}
          />
          <p className="pl-10 text-lg sm:text-xl text-[#1C1917] leading-relaxed font-normal">
            Because the options were guesswork, I was wide awake at 3am, and a few doctors who told me that it was just stress. Perimenopause throws confusing signals at you and there is no manual to navigate this phase. Supple watches your own data, finds the patterns, and shows you what&apos;s actually going on — so you can stop second-guessing yourself and start feeling like you again.
          </p>
          <p
            className="pl-10 mt-5 text-base sm:text-lg text-[#44403C]"
            data-testid="founder-note-read-more"
          >
            Read more{" "}
            <Link
              to="/why"
              className="font-semibold text-[#9A3F25] border-b border-[#9A3F25]/40 hover:border-[#9A3F25] transition-colors"
              data-testid="founder-note-read-more-link"
            >
              here
            </Link>
            .
          </p>
        </div>
        <p className="mt-8 pl-10 text-sm font-semibold tracking-wide text-[#9A3F25]">
          — Shruti, founder
        </p>
      </div>
    </section>
  );
}

// ───────────────── HOMEPAGE PULL-QUOTE (link to /why) ─────────────────
function HomepagePullQuote() {
  if (HIDE_HOME_PULL_QUOTE) return null;

  return (
    <section
      className="section-pad bg-bg"
      data-testid="homepage-pull-quote-section"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="relative">
          <Quote
            size={44}
            className="absolute -top-4 -left-2 text-[#EC6A4A]/25"
            strokeWidth={1}
          />
          <blockquote
            className="pl-10 sm:pl-14 font-display italic text-2xl sm:text-3xl lg:text-[2.25rem] text-[#1C1917]"
            style={{ lineHeight: 1.3 }}
            data-testid="homepage-pull-quote"
          >
            The women in my family were told their symptoms were normal —
            right up until the day the &lsquo;solution&rsquo; was a
            hysterectomy. Remove the organ, end the argument.
          </blockquote>
        </div>
        <div className="mt-8 pl-10 sm:pl-14">
          <Link
            to="/why"
            className="cta-pill cta-ghost"
            data-testid="homepage-read-why-link"
          >
            Read why we&apos;re building this <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      title: "Check Peri/Meno stage",
      body: "Answer 3 quick questions in 30 seconds.",
      action: "Start the check",
      href: "#check",
      testId: "how-it-works-stage",
    },
    {
      title: "Get Supplement Plan",
      body: "A plan for your next 8 weeks, based on your symptoms.",
      action: "See supplements",
      href: "/supplement-check",
      testId: "how-it-works-supplements",
    },
    {
      title: "Speak with a Pro",
      body: "Book a Free chat with your Peri/Meno Champion, then connect with a Doctor",
      action: "Book a free chat",
      href: "/doctor-request",
      testId: "how-it-works-doctor",
      showAction: DOCTOR_REQUEST_AVAILABLE,
    },
  ];

  return (
    <section className="section-pad home-how-it-works" data-testid="how-it-works-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <p className="home-how-it-works__label">How it works</p>
        <div className="home-how-it-works__grid">
          {steps.map((step) => (
            <article className="home-how-it-works__card" key={step.title} data-testid={step.testId}>
              <h2>{step.title}</h2>
              <p>{step.body}</p>
              {step.showAction !== false && (
                step.href.startsWith("/") ? (
                  <Link to={step.href} data-testid={`${step.testId}-button`}>{step.action}</Link>
                ) : (
                  <a href={step.href} data-testid={`${step.testId}-button`}>{step.action}</a>
                )
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ───────────────── SURVEY ─────────────────
function Survey() {
  return (
    <section
      className="section-pad"
      style={{ backgroundColor: "var(--surface-alt)" }}
      data-testid="survey-section"
    >
      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F1D9C6] bg-white/70 backdrop-blur-sm">
          <span className="sparkle" />
          <span className="text-xs font-semibold tracking-[0.16em] uppercase text-[#9A3F25]">
            Open · Takes 3 minutes
          </span>
        </div>
        <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]">
          Help shape Supple. <em>Tell us what you&apos;re living.</em>
        </h2>
        <p className="mt-6 text-base sm:text-lg text-[#44403C] leading-relaxed max-w-2xl mx-auto">
          We&apos;re talking to women in their 30s and 40s about what
          they&apos;re noticing, what&apos;s been dismissed, and what they
          wish someone had told them. Your answers go straight into how we
          build — anonymous, optional, and actually read.
        </p>
        <div className="mt-9 flex flex-col items-center gap-3">
          <a
            href={SURVEY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-pill cta-accent"
            data-testid="survey-cta-btn"
          >
            Take the 3-min survey <ArrowUpRight size={16} />
          </a>
          <p className="text-sm text-[#78716C]">
            Opens in Google Forms · we&apos;ll never share or sell your
            responses
          </p>
        </div>
      </div>
    </section>
  );
}

// ───────────────── WAITLIST FORM ─────────────────
function WaitlistForm() {
  return (
    <section
      id="waitlist"
      className="section-pad bg-bg"
      data-testid="waitlist-section"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="eyebrow mb-4">Join the waitlist</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]">
            Be among the first <em>to know yourself.</em>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#44403C] leading-relaxed">
            Founding members get full access during beta and the lowest
            founder pricing we&apos;ll ever offer when we open the doors.
          </p>
        </div>

        <div className="mt-10 supple-card max-w-xl mx-auto">
          <WaitlistFormCore testIdPrefix="waitlist" />
        </div>
      </div>
    </section>
  );
}

// ───────────────── STORIES ─────────────────
function StoryCard({ story, featured, onOpen, index }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(story.slug)}
      className={`group text-left supple-card overflow-hidden reveal ${
        featured ? "md:col-span-2 md:flex md:gap-6" : ""
      }`}
      style={{ animationDelay: `${index * 80}ms`, padding: 0 }}
      data-testid={`story-card-${story.slug}`}
    >
      <div
        className={`overflow-hidden ${
          featured ? "md:w-1/2 aspect-[16/10] md:aspect-auto" : "aspect-[16/10]"
        }`}
      >
        <img
          src={story.hero_image}
          alt={story.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className={`p-6 sm:p-7 ${featured ? "md:flex-1 md:p-8 md:flex md:flex-col md:justify-center" : ""}`}>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[10.5px] font-semibold tracking-[0.18em] uppercase text-[#9A3F25]">
            {story.tag}
          </span>
          <span className="text-[#A8A29E]">·</span>
          <span className="text-xs text-[#78716C]">{story.read_time}</span>
        </div>
        <h3
          className={`font-display font-medium text-[#1C1917] ${
            featured ? "text-3xl sm:text-4xl" : "text-2xl sm:text-[1.75rem]"
          }`}
          style={{ lineHeight: 1.12 }}
        >
          {story.title}
        </h3>
        <p className="mt-3 text-[#44403C] leading-relaxed text-[15px] sm:text-base line-clamp-3">
          {story.excerpt}
        </p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-[#9A3F25] font-semibold text-sm">
          Read story <ArrowRight size={14} />
        </span>
      </div>
    </button>
  );
}

function Stories({ stories, onOpenStory }) {
  const visibleStories = stories.filter((story) => !HIDDEN_HOME_STORY_SLUGS.has(story.slug));
  const featured = visibleStories.find((s) => s.featured) || visibleStories[0];
  const rest = visibleStories.filter((s) => s.slug !== featured?.slug);

  return (
    <section
      id="stories"
      className="section-pad bg-bg"
      data-testid="stories-section"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow mb-4">Stories</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]">
            Long-reads. <em>Real talk.</em>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#44403C] leading-relaxed">
            Essays from our founder on what perimenopause actually looks
            like — for us.
          </p>
        </div>

        {visibleStories.length === 0 ? (
          <p className="mt-10 text-[#78716C]">Stories are loading…</p>
        ) : (
          <>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {featured && (
                <StoryCard
                  story={featured}
                  featured
                  onOpen={onOpenStory}
                  index={0}
                />
              )}
              {rest.map((s, i) => (
                <StoryCard
                  key={s.slug}
                  story={s}
                  onOpen={onOpenStory}
                  index={i + 1}
                />
              ))}
            </div>
            <p className="mt-10 text-center">
              <Link
                to="/journal"
                className="cta-pill cta-ghost"
                data-testid="stories-view-all-link"
              >
                View all in Journal <ArrowRight size={16} />
              </Link>
            </p>
          </>
        )}
      </div>
    </section>
  );
}

// ───────────────── STORY READER (modal) ─────────────────
function StoryReader({ open, story, loading, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl w-[94vw] max-h-[90vh] overflow-y-auto p-0 bg-[#FFFBF6] border-[#F1D9C6]"
        data-testid="story-reader-dialog"
      >
        <DialogClose
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 border border-[#F1D9C6] inline-flex items-center justify-center hover:bg-white transition-colors"
          data-testid="story-reader-close"
        >
          <X size={16} />
        </DialogClose>

        {/* Visually hidden a11y title/description — radix requires them for screen readers */}
        <DialogTitle className="sr-only">
          {story?.title || "Story"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {story?.excerpt || "Reading a Supple story."}
        </DialogDescription>

        {loading || !story ? (
          <div className="p-10 text-center text-[#78716C]">Loading story…</div>
        ) : (
          <article data-testid={`story-article-${story.slug}`}>
            {story.hero_image && (
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={story.hero_image}
                  alt={story.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="px-6 sm:px-10 py-8 sm:py-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[10.5px] font-semibold tracking-[0.18em] uppercase text-[#9A3F25]">
                  {story.tag}
                </span>
                <span className="text-[#A8A29E]">·</span>
                <span className="text-xs text-[#78716C]">
                  {story.read_time}
                </span>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1C1917] leading-[1.1]">
                {story.title}
              </h1>
              <div className="mt-6 story-prose">
                {(story.body || []).map((block, i) => {
                  if (block.type === "p")
                    return <p key={i}>{block.text}</p>;
                  if (block.type === "italic")
                    return <p key={i} className="italic">{block.text}</p>;
                  if (block.type === "h3")
                    return <h3 key={i}>{block.text}</h3>;
                  if (block.type === "quote")
                    return <blockquote key={i}>{block.text}</blockquote>;
                  if (block.type === "list")
                    return (
                      <ul key={i}>
                        {(block.items || []).map((it, j) => (
                          <li key={j}>{it}</li>
                        ))}
                      </ul>
                    );
                  return null;
                })}
              </div>

              {story.survey_url && (
                <div
                  className="mt-10 p-6 sm:p-7 rounded-2xl border border-[#F1D9C6] bg-[#FFF4EC]"
                  data-testid="story-survey-cta"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={16} className="text-[#EC6A4A]" />
                    <span className="text-xs font-semibold tracking-[0.16em] uppercase text-[#9A3F25]">
                      Open · Takes 3 minutes
                    </span>
                  </div>
                  <h4 className="font-display text-2xl text-[#1C1917]">
                    Tell us what you&apos;re living.
                  </h4>
                  <p className="mt-2 text-[#44403C] text-[15px] leading-relaxed">
                    Three minutes. Anonymous. Actually shapes the product.
                  </p>
                  <a
                    href={story.survey_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-pill cta-accent mt-5"
                    data-testid="story-survey-link"
                  >
                    Take the 3-min survey <ArrowUpRight size={16} />
                  </a>
                </div>
              )}
            </div>
          </article>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ───────────────── FAQ ─────────────────
function FAQ() {
  return (
    <section
      className="section-pad"
      style={{ backgroundColor: "var(--surface-alt)" }}
      data-testid="faq-section"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center">
          <p className="eyebrow mb-4">FAQ</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]">
            Asked <em>&amp; answered.</em>
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          defaultValue="item-0"
          className="mt-10 space-y-3"
          data-testid="faq-accordion"
        >
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="bg-white rounded-2xl border border-[#F1D9C6] px-5 sm:px-6 overflow-hidden"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="font-display text-lg sm:text-xl font-medium text-[#1C1917] hover:no-underline py-5 text-left">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#44403C] leading-relaxed text-[15px] sm:text-base pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <p className="mt-8 text-center">
          <a href="/questions" className="home-faq-more-questions" data-testid="faq-more-questions-link">
            More questions →
          </a>
        </p>
      </div>
    </section>
  );
}

// ───────────────── FOOTER (imported) ─────────────────
// See /app/frontend/src/components/landing/Footer.jsx

// ───────────────── PAGE ─────────────────
export default function Landing() {
  useHomepageSEO(FAQ_ITEMS);
  const [stories, setStories] = useState([]);
  const [openSlug, setOpenSlug] = useState(null);
  const [activeStory, setActiveStory] = useState(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [showStickyCheck, setShowStickyCheck] = useState(true);

  useEffect(() => {
    let active = true;
    fetchStories()
      .then((data) => {
        if (active) setStories(data);
      })
      .catch(() => {
        if (active) setStories([]);
      });
    return () => {
      active = false;
    };
  }, [setStories]);

  useEffect(() => {
    if (!openSlug) {
      setActiveStory(null);
      return;
    }
    let active = true;
    setStoryLoading(true);
    fetchStory(openSlug)
      .then((s) => {
        if (active) {
          setActiveStory(s);
          setStoryLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setStoryLoading(false);
          toast.error("Couldn't load that story.");
        }
      });
    return () => {
      active = false;
    };
  }, [openSlug, setActiveStory, setStoryLoading]);

  function scrollToWaitlist() {
    const el = document.getElementById("waitlist");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function startStageCheck(event) {
    event.preventDefault();
    window.dispatchEvent(new Event("supple:start-stage-check"));
    window.requestAnimationFrame(() => {
      document.getElementById("check")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  useEffect(() => {
    const quiz = document.getElementById("check");
    if (!quiz) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCheck(!entry.isIntersecting),
      { threshold: 0.2 }
    );
    observer.observe(quiz);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="homepage bg-bg text-[#1C1917]" data-testid="landing-page">
      <Header onJoinClick={scrollToWaitlist} onStageCheck={startStageCheck} homepage />
      <main className="pt-0 homepage-sections">
        <div className="home-section home-section--hero"><Hero onJoinClick={scrollToWaitlist} onStageCheck={startStageCheck} /></div>
        <div className="home-section home-section--stats"><Stats /></div>
        <div className="home-section home-section--legacy-quiz"><ThirtySecondCheck /></div>
        <div className="home-section home-section--arc"><Arc /></div>
        <div className="home-section home-section--why"><WhySupple /></div>
        <div className="home-section home-section--founder"><FounderNote /></div>
        <div className="home-section home-section--pullquote"><HomepagePullQuote /></div>
        <div className="home-section home-section--survey"><Survey /></div>
        <div className="home-section home-section--waitlist"><WaitlistForm /></div>
        <div className="home-section home-section--stories"><Stories stories={stories} onOpenStory={(slug) => setOpenSlug(slug)} /></div>
        <div className="home-section home-section--faq"><FAQ /></div>
        <div className="home-section home-section--how"><HowItWorks /></div>
      </main>
      {showStickyCheck && (
        <a href="#check" onClick={startStageCheck} className="home-sticky-check" data-testid="mobile-sticky-check-button">
          Check my peri/meno stage
        </a>
      )}
      <Footer />
      <StoryReader
        open={!!openSlug}
        story={activeStory}
        loading={storyLoading}
        onClose={() => setOpenSlug(null)}
      />
    </div>
  );
}
